// File: frontend/src/pages/Auth/GoogleCallbackPage.jsx
// Google OAuth callback page - handles redirect from Google authentication

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const GoogleCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleGoogleCallback } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error

  useEffect(() => {
    const processCallback = async () => {
      try {
        console.log('🔐 Google OAuth callback page loaded');
        console.log('🔍 URL search params:', window.location.search);

        // Get token from URL parameters
        const token = searchParams.get('token');
        const error = searchParams.get('error');

        if (error) {
          console.error('❌ Error from OAuth callback:', error);
          setStatus('error');

          setTimeout(() => {
            navigate('/login', {
              replace: true,
              state: { error: getErrorMessage(error) }
            });
          }, 2000);
          return;
        }

        if (!token) {
          console.error('❌ No token found in callback URL');
          setStatus('error');

          setTimeout(() => {
            navigate('/login', {
              replace: true,
              state: { error: 'Token não encontrado na resposta de autenticação' }
            });
          }, 2000);
          return;
        }

        console.log('✅ Token found, processing...');
        console.log('🔑 Token:', token.substring(0, 20) + '...');

        // Handle the Google OAuth callback
        const result = handleGoogleCallback(token);

        if (result.success) {
          console.log('✅ Google OAuth callback processed successfully');
          setStatus('success');

          // Redirect to dashboard after brief delay
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
        } else {
          console.error('❌ Failed to process Google OAuth callback');
          setStatus('error');

          setTimeout(() => {
            navigate('/login', {
              replace: true,
              state: { error: 'Falha ao processar autenticação do Google' }
            });
          }, 2000);
        }

      } catch (error) {
        console.error('❌ Error processing Google OAuth callback:', error);
        setStatus('error');

        setTimeout(() => {
          navigate('/login', {
            replace: true,
            state: { error: 'Erro ao processar autenticação' }
          });
        }, 2000);
      }
    };

    processCallback();
  }, [searchParams, navigate, handleGoogleCallback]);

  const getErrorMessage = (error) => {
    const errorMessages = {
      'google_auth_failed': 'Falha na autenticação com Google',
      'authentication_failed': 'Falha na autenticação',
      'server_error': 'Erro no servidor',
      'access_denied': 'Acesso negado',
      'unauthorized_domain': 'Domínio de email não autorizado'
    };

    return errorMessages[error] || 'Erro desconhecido durante autenticação';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="mx-auto h-16 w-16 relative">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                  </svg>
                </div>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Processando autenticação...
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Aguarde enquanto validamos sua conta do Google
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="h-10 w-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Autenticação bem-sucedida!
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Redirecionando para o dashboard...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="h-10 w-10 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Erro na autenticação
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Redirecionando para a página de login...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCallbackPage;
